import { cn } from "@/lib/utils";

/**
 * The one sentence that has to sit near a score.
 *
 * It was written out three times — in the footer, at the foot of every report,
 * and on the methodology page — in three slightly different wrappers. Three
 * copies of a legal-ish sentence is how it drifts: change the wording once and
 * two stale versions stay in the build, both of them still being read.
 *
 * `variant` is placement, not decoration. `page` is the quiet rule-and-caption
 * at the bottom of a long page; `inline` is the boxed version for somewhere the
 * sentence has to interrupt rather than conclude.
 */
export default function ScoreDisclaimer({
  variant = "page",
  children,
  className = "",
  // A report renders one of these and so does the footer beneath it, so both
  // are in the DOM at once. A shared, hardcoded testid would be the only one in
  // the app that matches twice.
  testId = "score-disclaimer",
}) {
  const base = "text-xs leading-relaxed text-faint";
  const styles = {
    page: `mt-12 border-t border-line/10 pt-6 ${base}`,
    inline: `rounded-xl border border-line/10 bg-subtle p-4 ${base}`,
  };

  // cn, not template interpolation: a caller passing `mt-8` against the
  // variant's `mt-12` would otherwise leave both in the class list, and
  // Tailwind resolves that by stylesheet order rather than by the order of the
  // string — so the override would silently lose.
  return (
    <p className={cn(styles[variant] ?? styles.page, className)} data-testid={testId}>
      SlantScan scores are AI-generated estimates for educational purposes. They are not
      fact-checks and should not be treated as definitive.
      {children}
    </p>
  );
}
